import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

const AdventureModal = ({ adventure, onClose }) => {
    const [index, setIndex] = useState(0);
    const [isMobile, setIsMobile] = useState(false);

    // 📱 Detect mobile
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 1024);
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // 🎞️ Auto-slide every 3 seconds
    useEffect(() => {
        const interval = setInterval(() => {
            setIndex((prev) => (prev + 1) % adventure.images.length);
        }, 5000);
        return () => clearInterval(interval);
    }, [adventure.images.length]);


    // 🚫 Lock scroll visually but keep the page position (no jump)
    useEffect(() => {
        if (!isMobile) return;

        const scrollY = window.scrollY;
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.left = '0';
        document.body.style.right = '0';
        document.body.style.width = '100%';
        document.body.style.overflowY = 'hidden';

        return () => {
            const storedScrollY = parseInt(document.body.style.top || '0', 10) * -1;
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.left = '';
            document.body.style.right = '';
            document.body.style.width = '';
            document.body.style.overflowY = '';
            window.scrollTo(0, storedScrollY);
        };
    }, [isMobile]);


    // ✅ MOBILE VERSION (full-screen like PictureTextBlock)
    if (isMobile) {
        return ReactDOM.createPortal(
            <div
                style={{
                    position: 'fixed',
                    inset: 0,
                    backgroundColor: 'rgba(0,0,0,0.4)',
                    zIndex: 4000,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
                onClick={onClose}
            >
                <motion.div
                    initial={{ x: '-100%' }}
                    animate={{ x: 0 }}
                    exit={{ x: '100%' }}
                    transition={{ duration: 0.4, ease: 'easeInOut' }}
                    style={{
                        backgroundColor: '#D9D9D9',
                        width: '100vw',
                        height: '100dvh', // ✅ use dynamic viewport height instead of 100vh
                        borderRadius: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        position: 'relative',
                        paddingTop: 'env(safe-area-inset-top)', // ✅ respects iPhone notch area
                        paddingBottom: 'env(safe-area-inset-bottom)',
                        boxSizing: 'border-box',
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* 🖼️ Image on top */}
                    <div
                        style={{
                            height: '40vh',
                            position: 'relative',
                            overflow: 'hidden',
                        }}
                    >
                        <AnimatePresence mode="wait">
                            <motion.img
                                key={index}
                                src={adventure.images[index]}
                                alt={`${adventure.title} ${index + 1}`}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                }}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.4 }}
                            />
                        </AnimatePresence>

                        {/* ❌ White circle button inside image */}
                        <div
                            onClick={onClose}
                            style={{
                                position: 'absolute',
                                top: '15px',
                                right: '15px',
                                background: 'white',
                                borderRadius: '50%',
                                width: '40px',
                                height: '40px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 3px 8px rgba(0,0,0,0.25)',
                                cursor: 'pointer',
                                zIndex: 10,
                            }}
                        >
                            <X size={22} color="#5FA89B" />
                        </div>
                    </div>

                    {/* 📜 Scrollable text */}
                    <div
                        style={{
                            flex: 1,
                            overflowY: 'scroll',
                            padding: '40px 25px',
                            whiteSpace: 'pre-line',
                            scrollbarWidth: 'none',
                            msOverflowStyle: 'none',
                        }}
                    >
                        <style>{`::-webkit-scrollbar { display: none; }`}</style>
                        <h2
                            style={{
                                fontFamily: "'Libre Baskerville', serif",
                                fontSize: '26px',
                                fontWeight: '700',
                                color: '#000',
                                textAlign: 'center',
                                marginBottom: '25px',
                            }}
                        >
                            {adventure.title}
                        </h2>
                        <p
                            style={{
                                fontFamily: "'Libre Baskerville', serif",
                                fontSize: '17px',
                                lineHeight: 1.6,
                                color: '#000',
                                textAlign: 'justify',
                            }}
                        >
                            {adventure.description}
                        </p>
                    </div>
                </motion.div>
            </div>,
            document.body
        );
    }

    // 💻 DESKTOP VERSION (original overlay covering the pictures)
    const styles = {
        modal: {
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: '#D9D9D9',
            display: 'flex',
            borderRadius: '0px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.4)',
            zIndex: 10,
            overflow: 'hidden',
        },
        imageSection: {
            width: '55%',
            height: '100%',
            objectPosition: 'top',
            overflow: 'hidden',
            position: 'relative',
        },
        image: {
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRight: '2px solid #C0C0C0',
        },
        textSection: {
            width: '45%',
            display: 'flex',
            flexDirection: 'column',
            padding: '40px 60px',
            position: 'relative',
            overflowY: 'auto',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
        },
        title: {
            fontFamily: "'Libre Baskerville', serif",
            fontSize: '40px',
            fontWeight: '700',
            color: '#000000',
            marginBottom: '25px',
        },
        description: {
            fontFamily: "'Libre Baskerville', serif",
            fontSize: '20px',
            color: '#000000',
            lineHeight: 1.6,
            whiteSpace: 'pre-line',
        },
        closeButton: {
            position: 'absolute',
            top: '25px',
            right: '35px',
            cursor: 'pointer',
            color: '#5FA89B',
            background: 'rgba(255,255,255,0.9)',
            borderRadius: '50%',
            width: '45px',
            height: '45px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease',
            boxShadow: '0 3px 8px rgba(0,0,0,0.2)',
        },
    };

    return (
        <motion.div
            style={styles.modal}
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ duration: 0.6, ease: 'easeInOut' }}
        >
            {/* Left: Image slideshow */}
            <div style={styles.imageSection}>
                <AnimatePresence mode="wait">
                    <motion.img
                        key={index}
                        src={adventure.images[index]}
                        alt={`${adventure.title} ${index + 1}`}
                        style={styles.image}
                        initial={{ opacity: 0, scale: 1.05 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ duration: 0.8 }}
                    />
                </AnimatePresence>
            </div>

            {/* Right: Scrollable text */}
            <div style={styles.textSection} className="modal-text-scroll">
                <h2 style={styles.title}>{adventure.title}</h2>
                <p style={styles.description}>{adventure.description}</p>

                <motion.div
                    style={styles.closeButton}
                    whileHover={{
                        scale: 1.1,
                        background: 'rgba(255,255,255,1)',
                    }}
                    onClick={onClose}
                >
                    <X size={24} />
                </motion.div>
            </div>

            {/* Hide scrollbar inside modal text */}
            <style>
                {`
          .modal-text-scroll::-webkit-scrollbar { display: none; }
          .modal-text-scroll {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        `}
            </style>
        </motion.div>
    );
};

export default AdventureModal;